import { useRef, FormEvent } from 'react'
import { useTranslation } from 'next-i18next'

import loginAnalytics from '~/analytics/login/loginAnalytics'
import { ButtonPrimary } from '~/components/Buttons/ButtonPrimary'

import { InputV2 } from '~/components/InputV2'

import { useAppDispatch, useAppSelector } from '~/redux/hooks'

import useAuthenticateWithEmail from '~/auth/hooks/useAuthenticateWithEmail'

import { selectBackendError, selectFields, setFieldsActionCreator } from '~/auth/slices/login.slice'

import { useEmailInputValidationLocale } from '~/auth/hooks/useEmailInputValidationLocale'

import { LoginFields } from '../types/login.types'

import LoginServerError from './LoginServerError'
import { LoginExistingAccount } from './LoginExistingAccount'
import { RecoverPasswordButton } from './RecoverPasswordButton'

interface AuthenticateWithEmailProps {
  hidePassResetButton: boolean
}

export function AuthenticateWithEmail({ hidePassResetButton }: AuthenticateWithEmailProps): React.ReactElement {
  const { t } = useTranslation(['logIn', 'authenticate', 'fields'])
  const dispatch = useAppDispatch()
  const fields = useAppSelector(selectFields)
  const backendError = useAppSelector(selectBackendError)
  const { handleAuthenticateWithEmail, isLoading } = useAuthenticateWithEmail()
  const ref = useRef<HTMLInputElement>(null)

  const handleOnSubmit = (evt: FormEvent): void => {
    loginAnalytics.logButtonClick('login')
    handleAuthenticateWithEmail(evt)
  }

  const handleOnChangeTextFields = (name: string, value: string): void => {
    // track only when the first character is placed
    if (fields[name as keyof LoginFields] === '' && value !== '') {
      loginAnalytics.writingInForms(name)
    }
    dispatch(setFieldsActionCreator({ ...fields, [name]: value }))
  }

  useEmailInputValidationLocale(fields.email, ref)

  return (
    <div className="w-full lg:w-125 h-full space-y-4 flex flex-col items-center justify-center max-w-screen-19/20">
      <div className="text-h3 font-bold">{t('logIn:title')}</div>
      {backendError && (
        <div className="bg-white w-full rounded-large border-1">
          <LoginServerError message={backendError} />
        </div>
      )}
      <form className="w-full flex flex-col max-w-screen-19/20" onSubmit={handleOnSubmit}>
        <InputV2
          autoComplete="email"
          label={t('fields:email')}
          name="email"
          ref={ref}
          setValue={(val) => handleOnChangeTextFields('email', val)}
          type="email"
          value={fields.email}
        />

        <InputV2
          autoComplete="password"
          label={t('fields:password')}
          name="password"
          setValue={(val) => handleOnChangeTextFields('password', val)}
          type="password"
          value={fields.password}
        />

        <ButtonPrimary
          className="border-2 border-white font-semibold"
          disabled={!fields.email || !fields.password}
          disableRing
          isFullWidth
          isLoading={isLoading}
          size="small"
          type="submit"
        >
          {t('authenticate:logIn')}
        </ButtonPrimary>
      </form>
      <LoginExistingAccount />

      {!hidePassResetButton && <RecoverPasswordButton />}
    </div>
  )
}
